import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import {
  JsonEditorComponent,
  SearchableSingleSelectorComponent,
  SearchableSingleSelectorItem,
  VerticalExpandAnimation,
} from '@shared/ui';

import { LocalizedChipsInputComponent } from '../localized-chips-input/localized-chips-input.component';

/**
 * Dumb "Acquisition" section of the method-type editor: the asset-type
 * selector and, below it, either the predefined sample-names chips input or —
 * once manual config mode is on — the raw JSON config editor. It holds no
 * state and performs no logic: every input is forwarded to the matching child,
 * every child output is re-emitted verbatim, and the section swap is driven by
 * the `manualConfigMode` input. The smart editor owns the activation warning
 * dialog (the button here only emits `manualConfigRequested`); the config
 * panel's exit button mirrors that split — enablement arrives via
 * `canExitManualConfig` and the click only emits `manualConfigExitRequested`.
 *
 * The two blocks share the `@collapse` (vertical-expand) animation, so the
 * sample-names block collapses while the config block expands in its place.
 */
@Component({
  selector: 'ts-method-type-acquisition-form',
  templateUrl: './method-type-acquisition-form.component.html',
  styleUrls: ['./method-type-acquisition-form.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [VerticalExpandAnimation],
  imports: [
    CommonModule,
    TranslateModule,
    MatTooltipModule,
    SearchableSingleSelectorComponent,
    LocalizedChipsInputComponent,
    JsonEditorComponent,
  ],
})
export class MethodTypeAcquisitionFormComponent {
  @Input() assetTypeOptions: SearchableSingleSelectorItem[] = [];
  @Input() assetTypesLoaded = false;
  @Input() selectedAssetType: SearchableSingleSelectorItem | null = null;
  @Input() assetTypeSearchTerm = '';

  /** i18n key painting the asset-type selector's error state; null renders no error. */
  @Input() assetTypeErrorKey: string | null = null;

  @Input() sampleNames: Record<string, string>[] = [];
  @Input() currentLanguage = 'en';

  /** True swaps the sample-names block for the manual JSON config editor. */
  @Input() manualConfigMode = false;
  /** One-time seed for the JSON editor (the active config object, else null). */
  @Input() methodTypeConfig: Record<string, unknown> | null = null;
  /** i18n key painting the config section's save-validation error; null renders none. */
  @Input() configErrorKey: string | null = null;
  /** True enables the exit-manual-mode button (the draft is conventional again). */
  @Input() canExitManualConfig = false;

  @Output() assetTypeSelected = new EventEmitter<SearchableSingleSelectorItem>();
  @Output() assetTypeSearchChanged = new EventEmitter<string>();
  @Output() assetTypeLastItemReached = new EventEmitter<void>();
  @Output() sampleNameAdded = new EventEmitter<Record<string, string>>();
  @Output() sampleNameRemoved = new EventEmitter<number>();
  @Output() sampleNameUpdated = new EventEmitter<{ index: number; values: Record<string, string> }>();

  /** The user asked to enable manual mode */
  @Output() manualConfigRequested = new EventEmitter<void>();
  /** The user asked to exit manual mode back to the chips */
  @Output() manualConfigExitRequested = new EventEmitter<void>();
  /** The JSON editor's latest parse state, forwarded verbatim. */
  @Output() configChanged = new EventEmitter<{ value: unknown | null; valid: boolean }>();
}
